import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { forkJoin, of, Subject } from 'rxjs';
import { GitlabService } from '../gitlab.service';
import { PipelineDetail } from './pipeline-detail';

/**
 * Dashboard component that shows various information on the runners and pipelines
 * Due to the nature of gitlab and the way it needs to load data we have no other choice
 * than the load the majority of data through this component and forward it to the child components
 * so we can re-use the majority of data loads
 */
@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  gitlabInstanceName: string;

  timeoutHandle: any;
  runners: any[] = [];
  runningPipelines: PipelineDetail[] = [];

  private runningPipelinesChange: Subject<PipelineDetail[]> = new Subject<PipelineDetail[]>();
  private visiblePipelinesChange: Subject<PipelineDetail[]> = new Subject<PipelineDetail[]>();

  constructor(private gitlabService: GitlabService) {
  }

  ngOnInit() {
    // triggered when the running pipeline change data
    this.runningPipelinesChange.asObservable().subscribe(pipelines => this.loadPipelines(pipelines));
    this.visiblePipelinesChange.asObservable().subscribe(pipelines => this.loadPipelineViewData(pipelines));
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.timeoutHandle) {
      window.clearTimeout(this.timeoutHandle);
    }

    this.loadDashboard();
  }


  ngOnDestroy() {
    window.clearTimeout(this.timeoutHandle);
  }


  private loadDashboard() {
    this.loadRunners();

    this.timeoutHandle = setTimeout(() => this.loadDashboard(), 30_000);
  }

  private loadRunners() {
    if (this.gitlabInstanceName && this.gitlabInstanceName !== '') {
      this.gitlabService.getRunners(this.gitlabInstanceName)
        .subscribe(runners => {

          let runnerJobRequests = runners.map(
            runner => runner.active ? this.gitlabService.getRunnerJobs(this.gitlabInstanceName, runner.id) : of([]));

          forkJoin(runnerJobRequests).subscribe(runnerJobs => {

            let allRunningJobs = [];
            for (let i = 0; i < runners.length; i++) {
              runners[i].jobs = runnerJobs[i];
              allRunningJobs.push(...runnerJobs[i]);
            }

            let pipelines: PipelineDetail[] = [];
            allRunningJobs.forEach(job => {
              if (!pipelines.find(p => p.id == job.pipeline.id)) {
                pipelines.push(new PipelineDetail(job));
              }
            });

            console.info('loading runner data completed');
            this.runners = runners;
            this.runningPipelinesChange.next(pipelines);
          });
        });
    }
    else {
      this.runners = [];
    }
  }

  private loadPipelines(pipelines: PipelineDetail[]) {
    // ensure that we retain old pipelines which are not there anymore but have been active in the last 2 minutes
    let retain = this.runningPipelines.filter(p => pipelines.find(predicate => predicate.id == p.id) ||
      ((new Date(p.pipeline.updated_at).getTime() + 120000) > new Date().getTime()));

    let reload = [];
    retain.forEach(retain => {
      if (!pipelines.find(p => p.id == retain.id)) {
        reload.push(this.gitlabService.getPipeline(this.gitlabInstanceName, retain.project.id, retain.id));
      }
    });

    if (reload.length > 0) {
      forkJoin(reload).subscribe(reloaded => {
        retain.forEach(retain => {
          let pipeline = reloaded.find(r => r.id == retain.id);
          if (pipeline) {
            retain.pipeline = pipeline;
            pipelines.push(retain);
          }
        });

        pipelines = pipelines.sort((p1, p2) => p1.id - p2.id);

        // not done yet, need to enrich data
        console.info('loading visible pipelines completed');
        this.visiblePipelinesChange.next(pipelines);
      });
    }
    else {
      pipelines = pipelines.sort((p1, p2) => p1.id - p2.id);

      // not done yet, need to enrich data
      console.info('loading visible pipelines completed');
      this.visiblePipelinesChange.next(pipelines);
    }
  }

  private loadPipelineViewData(pipelines: PipelineDetail[]) {
    // need to load jobs and test reports
    let requests = [];

    requests.push(...pipelines.map(p => this.gitlabService.getPipelineJobs(this.gitlabInstanceName, p.project.id, p.id)));
    requests.push(...pipelines.map(p => this.gitlabService.getPipelineTestReport(this.gitlabInstanceName, p.project.id, p.id)));

    if (requests.length > 0) {
      forkJoin(requests).subscribe(result => {
        for (let i = 0; i < pipelines.length; i++) {
          pipelines[i].jobs = result[i];
          pipelines[i].testSummary = result[i + pipelines.length];
        }

        // finally done
        console.info('loading pipeline detail completed');
        this.runningPipelines = pipelines;
      });
    }
    else {
      // finally done
      this.runningPipelines = pipelines;
    }
  }
}
